import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  FileText, 
  Calendar, 
  DollarSign, 
  Users, 
  Building, 
  Tag, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  XCircle,
  Archive,
  Copy,
  Trash2
} from 'lucide-react';
import { getContract, copyContract, deleteContract, getContractAdminFees, accrueContractAdminFee } from '../../config/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();

  // Fetch contract details
  const { 
    data: contract, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => getContract(id),
    enabled: !!id && !!user,
    staleTime: 300000, // Cache for 5 minutes
  });

  // Copy contract mutation
  const copyMutation = useMutation({
    mutationFn: ({ id, copyData }) => copyContract(id, copyData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract copied successfully!');
      navigate(`/app/contracts/${data.contract.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to copy contract');
    }
  });

  // Delete contract mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => deleteContract(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(`Contract deleted successfully! Removed ${data.deletedData.prices} prices, ${data.deletedData.customers} customers, and ${data.deletedData.aliases} aliases.`);
      navigate('/app/contracts');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete contract');
    }
  });

  const handleCopyContract = () => {
    if (!contract) return;
    const newNumber = prompt(`Enter new contract number for "${contract.number}":`, `${contract.number}-COPY`);
    if (newNumber && newNumber.trim()) {
      const copyData = {
        number: newNumber.trim(),
        description: `${contract.description} (Copy)`
      };
      copyMutation.mutate({ id: contract.id, copyData });
    }
  };

  const handleDeleteContract = () => {
    if (!contract) return;
    const priceCount = contract.prices?.length || 0;
    const customerCount = contract.contractCustomers?.length || 0;
    const aliasCount = contract.aliases?.length || 0;
    const claimCount = contract.chargebackClaims?.length || 0;
    
    if (claimCount > 0) {
      toast.error('Cannot delete contract with existing chargeback claims. Please resolve all claims first.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete contract "${contract.number}"?\n\nThis will permanently delete:\n- ${priceCount} product prices\n- ${customerCount} customer relationships\n- ${aliasCount} product aliases\n- All contract data\n\nThis action cannot be undone.`)) {
      deleteMutation.mutate(contract.id);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Contract</h2>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load contract details'}</p>
          <button
            onClick={() => navigate('/app/contracts')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText size={64} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contract Not Found</h2>
          <p className="text-gray-600 mb-4">The requested contract could not be found.</p>
          <button
            onClick={() => navigate('/app/contracts')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };



  // Helper function to get status info
  const getStatusInfo = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Active' };
      case 'DRAFT':
        return { icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Draft' };
      case 'EXPIRED':
        return { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Expired' };
      case 'ARCHIVED':
        return { icon: Archive, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Archived' };
      default:
        return { icon: Clock, color: 'text-gray-600', bgColor: 'bg-gray-100', label: status || 'Unknown' };
    }
  };

  // Helper function to get type info
  const getTypeInfo = (type) => {
    switch (type?.toUpperCase()) {
      case 'DIRECT':
        return { color: 'bg-blue-100 text-blue-800', label: 'Direct' };
      case 'INDIRECT':
        return { color: 'bg-purple-100 text-purple-800', label: 'Indirect' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: type || 'Unknown' };
    }
  };

  const statusInfo = getStatusInfo(contract.status);
  const typeInfo = getTypeInfo(contract.type);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/app/contracts')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                title="Back to Contracts"
              >
                <ArrowLeft size={20} />
                <span>Back to Contracts</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <FileText size={24} className="text-primary-600" />
                <h1 className="text-3xl font-bold text-gray-900">Contract Details</h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/app/contracts/${id}/edit`)}
                className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                title="Edit Contract"
              >
                <Edit size={16} />
                <span>Edit Contract</span>
              </button>
              <button
                onClick={handleCopyContract}
                disabled={copyMutation.isPending}
                className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Copy Contract"
              >
                <Copy size={16} />
                <span>{copyMutation.isPending ? 'Copying...' : 'Copy Contract'}</span>
              </button>
              {hasRole('ADMIN') && (
                <button
                  onClick={handleDeleteContract}
                  disabled={deleteMutation.isPending}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete Contract"
                >
                  <Trash2 size={16} />
                  <span>{deleteMutation.isPending ? 'Deleting...' : 'Delete Contract'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contract Information Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Contract Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <FileText size={20} className="text-primary-600" />
                <span>Basic Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contract Number</label>
                  <p className="text-gray-900 font-medium">{contract.number || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900">{contract.description || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.bgColor} ${statusInfo.color}`}>
                    <statusInfo.icon size={14} className="mr-1" />
                    {statusInfo.label}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <p className="text-gray-900">{contract.class || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                  <p className="text-gray-900">{contract.group || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <p className="text-gray-900">{contract.priority || 'Not set'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class of Trade</label>
                  <p className="text-gray-900">{contract.classOfTrade || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Effective Dates */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Calendar size={20} className="text-primary-600" />
                <span>Effective Dates</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
                  <p className="text-gray-900">{formatDate(contract.effectiveFrom)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
                  <p className="text-gray-900">{formatDate(contract.effectiveTo)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <p className="text-gray-900">{formatDate(contract.createdAt)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <p className="text-gray-900">{formatDate(contract.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* GPO Information */}
            {contract.gpo && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Building size={20} className="text-primary-600" />
                  <span>GPO Information</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPO Name</label>
                    <p className="text-gray-900">{contract.gpo.name || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPO ID</label>
                    <p className="text-gray-900 font-mono">{contract.gpo.id || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Contract Terms */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contract Terms</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Contract Name</span><p className="text-gray-900">{contract.contractName || '—'}</p></div>
                <div><span className="text-gray-500">Version</span><p className="text-gray-900">{contract.contractVersion || '—'}</p></div>
                <div><span className="text-gray-500">Payment Terms</span><p className="text-gray-900">{contract.paymentTerms || '—'}</p></div>
                <div><span className="text-gray-500">Renewal Date</span><p className="text-gray-900">{contract.renewalDate ? formatDate(contract.renewalDate) : '—'}</p></div>
                <div><span className="text-gray-500">Termination Date</span><p className="text-gray-900">{contract.terminationDate ? formatDate(contract.terminationDate) : '—'}</p></div>
                <div><span className="text-gray-500">Auto Renewal</span><p className="text-gray-900">{contract.autoRenewal ? 'Yes' : 'No'}</p></div>
                <div><span className="text-gray-500">Base Discount</span><p className="text-gray-900">{contract.baseDiscount != null ? contract.baseDiscount + '%' : '—'}</p></div>
                <div><span className="text-gray-500">Volume Discount</span><p className="text-gray-900">{contract.volumeDiscount != null ? contract.volumeDiscount + '%' : '—'}</p></div>
                <div><span className="text-gray-500">Prompt Pay Discount</span><p className="text-gray-900">{contract.promptPayDiscount != null ? contract.promptPayDiscount + '%' : '—'}</p></div>
                <div><span className="text-gray-500">Admin Fee</span><p className="text-gray-900">{contract.adminFee != null ? contract.adminFee + '%' : '—'}</p></div>
                <div><span className="text-gray-500">Chargeback Fee</span><p className="text-gray-900">{contract.chargebackFee != null ? contract.chargebackFee + '%' : '—'}</p></div>
                <div><span className="text-gray-500">Credit Limit</span><p className="text-gray-900">{contract.creditLimit ?? '—'}</p></div>
                <div><span className="text-gray-500">Min / Max Order</span><p className="text-gray-900">{(contract.minimumOrder ?? '—')} / {(contract.maximumOrder ?? '—')}</p></div>
                <div><span className="text-gray-500">EDI</span><p className="text-gray-900">{contract.ediEnabled ? ((contract.ediFormat || 'X12') + ' ' + (contract.ediVersion || '')).trim() : 'Disabled'}</p></div>
                <div><span className="text-gray-500">Compliance / Audit / Reporting</span><p className="text-gray-900">{[contract.complianceRequired && 'Compliance', contract.auditRequired && 'Audit', contract.reportingRequired && 'Reporting'].filter(Boolean).join(', ') || 'None'}</p></div>
              </div>
              {contract.notes && <div className="mt-4"><span className="text-gray-500 text-sm">Notes</span><p className="text-gray-900 text-sm">{contract.notes}</p></div>}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contract Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Tag size={20} className="text-primary-600" />
                <span>Contract Summary</span>
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Type</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Status</span>
                  <span className={`text-sm font-medium ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Priority</span>
                  <span className="text-sm font-medium text-purple-600">
                    {contract.priority || 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/app/contracts/${id}/pricing`)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
                  title="Manage Pricing"
                >
                  <DollarSign size={16} className="mr-2" />
                  Manage Pricing
                </button>
                
                <button
                  onClick={() => navigate(`/app/contracts/${id}/customers`)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  title="Manage Customers"
                >
                  <Users size={16} className="mr-2" />
                  Manage Customers
                </button>
                
                <button
                  onClick={() => navigate(`/app/contracts/${id}/aliases`)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                  title="Manage Aliases"
                >
                  <Tag size={16} className="mr-2" />
                  Manage Aliases
                </button>
              </div>
            </div>
          </div>
        </div>

        <AdminFeesSection contractId={id} />
      </div>
    </div>
  );
};

// GPO admin-fee accrual: pick a period, accrue (rate% x eligible member net
// sales), and see the running statement. Ties to point-in-time GPO membership.
function AdminFeesSection({ contractId }) {
  const qc = useQueryClient();
  const year = new Date().getFullYear();
  const [from, setFrom] = React.useState(`${year}-01-01`);
  const [to, setTo] = React.useState(`${year}-12-31`);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-fees', contractId],
    queryFn: () => getContractAdminFees(contractId)
  });

  const accrue = useMutation({
    mutationFn: () => accrueContractAdminFee(contractId, { periodStart: from, periodEnd: to }),
    onSuccess: (res) => {
      toast.success(`Accrued $${Number(res?.feeAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
      qc.invalidateQueries({ queryKey: ['admin-fees', contractId] });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to accrue admin fee')
  });

  const accruals = data?.accruals || [];
  const totalFees = data?.totalFees || 0;
  const money = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => new Date(d).toLocaleDateString();

  return (
    <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 no-card-hover">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <DollarSign size={20} className="text-primary-600" /> GPO Admin Fees
        </h3>
        <div className="flex items-end gap-2">
          <label className="text-xs text-gray-500">From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="block mt-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-gray-500">To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="block mt-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
          </label>
          <button onClick={() => accrue.mutate()} disabled={accrue.isPending}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
            {accrue.isPending ? 'Accruing…' : 'Accrue period'}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Admin fee = contract admin-fee rate × eligible member net sales in the period (active GPO members + the contract's direct roster). Paid to the GPO — a gross-to-net component, distinct from the wholesaler chargeback.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : accruals.length === 0 ? (
        <p className="text-sm text-gray-400">No accruals yet. Pick a period and click “Accrue period”.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                <th className="px-2 py-1.5 font-medium">Period</th>
                <th className="px-2 py-1.5 font-medium">Rate</th>
                <th className="px-2 py-1.5 font-medium">Eligible sales</th>
                <th className="px-2 py-1.5 font-medium">Members</th>
                <th className="px-2 py-1.5 font-medium">Fee</th>
                <th className="px-2 py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {accruals.map((a) => (
                <tr key={a.id} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(a.periodStart)} – {fmtDate(a.periodEnd)}</td>
                  <td className="px-2 py-1.5">{Number(a.feeRatePercent || 0).toFixed(2)}%</td>
                  <td className="px-2 py-1.5">{money(a.eligibleSales)}</td>
                  <td className="px-2 py-1.5">{a.memberCount}</td>
                  <td className="px-2 py-1.5 font-semibold text-gray-900">{money(a.feeAmount)}</td>
                  <td className="px-2 py-1.5"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{a.status}</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold text-gray-900">
                <td className="px-2 py-1.5" colSpan={4}>Total accrued</td>
                <td className="px-2 py-1.5">{money(totalFees)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContractDetail;
